/**
 * Admin Service
 * API calls for system admin functionality
 */

import { get, put, del, post } from './api';
import type {
  OrganizationListResponse,
  OrganizationDetail,
  OrganizationStats,
  UpdateOrganizationRequest,
  CreateOrganizationRequest,
  CreateOrganizationResponse,
  AdminUser,
  AdminCreateUserRequest,
  AdminCreateUserResponse,
  GetOrganizationsParams,
  PauseOrganizationResponse,
  SetOrganizationTrialRequest,
  OrganizationTrialResponse,
  AdminGetStartedSettings,
} from '../types/admin';
import { toSampleDataOutcome } from '../utils/sampleDataStatus';

/**
 * Get paginated list of all organizations
 */
export const getOrganizations = (params: GetOrganizationsParams = {}): Promise<OrganizationListResponse> => {
  const queryParams = new URLSearchParams();

  if (params.pageNumber) {
    queryParams.append('pageNumber', params.pageNumber.toString());
  }
  if (params.pageSize) {
    queryParams.append('pageSize', params.pageSize.toString());
  }
  if (params.search) {
    queryParams.append('search', params.search);
  }

  const queryString = queryParams.toString();
  const url = `/api/admin/organizations${queryString ? `?${queryString}` : ''}`;

  return get<OrganizationListResponse>(url);
};

/**
 * Get organization details by ID
 */
export const getOrganization = (orgId: string): Promise<OrganizationDetail> => {
  return get<OrganizationDetail>(`/api/admin/organizations/${orgId}`);
};

/**
 * Get organization statistics
 */
export const getOrganizationStats = (orgId: string): Promise<OrganizationStats> => {
  return get<OrganizationStats>(`/api/admin/organizations/${orgId}/stats`);
};

/**
 * Create a new organization
 */
export const createOrganization = async (
  data: CreateOrganizationRequest
): Promise<CreateOrganizationResponse> => {
  const response = await post<OrganizationDetail & { sampleData?: unknown }>(
    '/api/admin/organizations',
    data
  );
  // Only a recognised outcome survives, so an older or newer API never renders a wrong message.
  return { ...response, sampleData: toSampleDataOutcome(response?.sampleData) };
};

/**
 * Update organization details
 */
export const updateOrganization = (
  orgId: string,
  data: UpdateOrganizationRequest
): Promise<OrganizationDetail> => {
  return put<OrganizationDetail>(`/api/admin/organizations/${orgId}`, data);
};

/**
 * Delete an organization (soft delete)
 */
export const deleteOrganization = (orgId: string): Promise<{ message: string }> => {
  return del<{ message: string }>(`/api/admin/organizations/${orgId}`);
};

/**
 * apiRequest only wires its AbortController when a timeout is supplied, so without this a
 * stalled pause/resume request never settles and leaves the confirm modal stuck on
 * "Pausing..." with no way to retry.
 */
const PAUSE_REQUEST_TIMEOUT_MS = 15000;

/**
 * Pause an organization (reversible billing suspension).
 * Pass a message to override the default copy shown to that org's users.
 */
export const pauseOrganization = (
  orgId: string,
  message?: string | null
): Promise<PauseOrganizationResponse> => {
  return post<PauseOrganizationResponse>(
    `/api/admin/organizations/${orgId}/pause`,
    { message: message ?? null },
    { timeout: PAUSE_REQUEST_TIMEOUT_MS }
  );
};

/**
 * Unpause a previously paused organization
 */
export const unpauseOrganization = (orgId: string): Promise<PauseOrganizationResponse> => {
  return post<PauseOrganizationResponse>(
    `/api/admin/organizations/${orgId}/unpause`,
    undefined,
    { timeout: PAUSE_REQUEST_TIMEOUT_MS }
  );
};

/**
 * Same reasoning as the pause timeout above: without one, apiRequest never wires its
 * AbortController and a stalled trial request leaves the dialog on "Saving..." for good.
 */
const TRIAL_REQUEST_TIMEOUT_MS = 15000;

/**
 * Set, extend or clear an organization's trial window.
 *
 * Pass both dates to set or extend one, and both as null to clear it. The API refuses a single
 * date with a 400, and refuses any change to an organization that has already converted with a
 * 409 — see `trialFailureMessage` for how each refusal is put to a system admin.
 */
export const setOrganizationTrial = (
  orgId: string,
  request: SetOrganizationTrialRequest
): Promise<OrganizationTrialResponse> => {
  return put<OrganizationTrialResponse>(
    `/api/admin/organizations/${orgId}/trial`,
    // Field by field rather than spread, so the payload cannot grow a status or a day count that
    // the server owns and would ignore.
    {
      trialStartedAt: request.trialStartedAt,
      trialEndsAt: request.trialEndsAt,
    },
    { timeout: TRIAL_REQUEST_TIMEOUT_MS }
  );
};

/**
 * Remove an organization's trial entirely.
 *
 * The same endpoint as setting one, with neither date. It also clears a recorded conversion, which
 * is the only route back from a conversion marked by mistake — at the cost of the original window,
 * which is not kept.
 */
export const clearOrganizationTrial = (
  orgId: string
): Promise<OrganizationTrialResponse> => {
  return setOrganizationTrial(orgId, {
    trialStartedAt: null,
    trialEndsAt: null,
  });
};

/**
 * Record that an organization became a paying customer.
 *
 * Idempotent: converting an already-converted organization keeps the first conversion instant
 * rather than moving it to now.
 */
export const convertOrganizationTrial = (
  orgId: string
): Promise<OrganizationTrialResponse> => {
  return post<OrganizationTrialResponse>(
    `/api/admin/organizations/${orgId}/trial/convert`,
    undefined,
    { timeout: TRIAL_REQUEST_TIMEOUT_MS }
  );
};

/**
 * Get users for a specific organization
 */
export const getOrganizationUsers = (orgId: string): Promise<AdminUser[]> => {
  return get<AdminUser[]>(`/api/admin/organizations/${orgId}/users`);
};

/**
 * Create a user for a specific organization
 */
export const createUserForOrganization = (
  orgId: string,
  data: AdminCreateUserRequest
): Promise<AdminCreateUserResponse> => {
  return post<AdminCreateUserResponse>(`/api/admin/organizations/${orgId}/users`, data);
};

/**
 * Delete a user from an organization
 */
export const deleteUserFromOrganization = (
  orgId: string,
  userId: string
): Promise<{ message: string }> => {
  return del<{ message: string }>(`/api/admin/organizations/${orgId}/users/${userId}`);
};


/**
 * Change a member's role within an organization. System admin only.
 *
 * The organization is in the path rather than taken from the caller's claim: a system admin's own
 * organization is not the one they are looking at, so the org-facing `/api/users/{id}/role` could
 * never reach these users. Both routes share the same server-side guards.
 */
export const updateOrganizationUserRole = (
  orgId: string,
  userId: string,
  role: string
): Promise<AdminUser> => {
  return put<AdminUser>(`/api/admin/organizations/${orgId}/users/${userId}/role`, {
    role,
  });
};

const GET_STARTED_SETTINGS_URL = '/api/admin/get-started/settings';

/**
 * On unless the API says otherwise, because on is the API's default. A missing or malformed flag
 * reading as "off" would tell an admin nobody sees Get Started when everyone does.
 */
export const normalizeGetStartedSettings = (raw: unknown): AdminGetStartedSettings => {
  const record = typeof raw === 'object' && raw !== null && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : {};
  return {
    enabled: typeof record.enabled === 'boolean' ? record.enabled : true,
    updatedAt:
      typeof record.updatedAt === 'string' && record.updatedAt.length > 0 ? record.updatedAt : null,
  };
};

/** Whether anyone sees the Get Started checklist. Throws `ApiError`; a 404 means an older API. */
export const getGetStartedSettings = async (
  signal?: AbortSignal
): Promise<AdminGetStartedSettings> =>
  normalizeGetStartedSettings(await get<unknown>(GET_STARTED_SETTINGS_URL, { signal }));

/** Turns the Get Started checklist on or off for everyone. Throws `ApiError`. */
export const updateGetStartedSettings = async (
  enabled: boolean,
  signal?: AbortSignal
): Promise<AdminGetStartedSettings> =>
  normalizeGetStartedSettings(
    await put<unknown>(GET_STARTED_SETTINGS_URL, { enabled }, { signal })
  );
